import { Card } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import { Badge } from '../../../shared/components/ui/badge';
import { Trash2, Mail, Briefcase, UserCircle } from 'lucide-react';
import { NetworkConnection } from '../api/networkApi';

type NetworkConnectionCardProps = {
  connection: NetworkConnection;
  onViewProfile: (connection: NetworkConnection) => void;
  onRemove: (targetUserId: string, displayName: string) => void;
  isRemoving: boolean;
};

const getContactName = (connection: NetworkConnection) => {
  const { firstName, lastName } = connection.target;
  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;
  if (lastName) return lastName;
  return null;
};

const getDisplayName = (connection: NetworkConnection) => {
  return (
    connection.target.serviceProvider?.companyName ||
    connection.target.financialAdvisor?.companyName ||
    getContactName(connection) ||
    'Unknown'
  );
};

const getRoleBadge = (role: string) => {
  if (role === 'SERVICE_PROVIDER') {
    return (
      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
        <Briefcase className="w-3 h-3 mr-1" />
        Service Provider
      </Badge>
    );
  }
  if (role === 'FINANCIAL_ADVISOR') {
    return (
      <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
        <UserCircle className="w-3 h-3 mr-1" />
        Financial Advisor
      </Badge>
    );
  }
  return null;
};

export const NetworkConnectionCard = ({
  connection,
  onViewProfile,
  onRemove,
  isRemoving,
}: NetworkConnectionCardProps) => {
  const isServiceProvider = connection.target.role === 'SERVICE_PROVIDER';
  const serviceProvider = connection.target.serviceProvider;
  const financialAdvisor = connection.target.financialAdvisor;
  const displayName = getDisplayName(connection);

  return (
    <Card className="p-6 hover:shadow-lg transition-shadow">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Professional Info */}
        <div className="flex-1">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl text-gray-900">{displayName}</h3>
                {getRoleBadge(connection.target.role)}
              </div>
              <p className="text-gray-600">{getContactName(connection) || connection.target.email}</p>
            </div>
          </div>

          {/* Categories (SP only) */}
          {isServiceProvider && serviceProvider && (
            <>
              <div className="flex flex-wrap gap-2 mb-3">
                {serviceProvider.categories.map(
                  (cat: { category: { id: string; name: string } }) => (
                    <Badge
                      key={cat.category.id}
                      variant="secondary"
                      className="bg-[#026857]/10 text-[#026857] hover:bg-[#026857]/20"
                    >
                      {cat.category.name}
                    </Badge>
                  ),
                )}
              </div>

              {/* Specialties (SP only) */}
              {serviceProvider.specialties.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {serviceProvider.specialties
                    .slice(0, 3)
                    .map((spec: { specialty: { id: string; name: string } }) => (
                      <Badge key={spec.specialty.id} variant="outline" className="text-sm">
                        {spec.specialty.name}
                      </Badge>
                    ))}
                  {serviceProvider.specialties.length > 3 && (
                    <Badge variant="outline" className="text-sm">
                      +{serviceProvider.specialties.length - 3} more
                    </Badge>
                  )}
                </div>
              )}
            </>
          )}

          {/* FA Company Info */}
          {!isServiceProvider && financialAdvisor?.companyName && (
            <p className="text-gray-600 mb-3">{financialAdvisor.companyName}</p>
          )}

          {/* Contact Info */}
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <Mail className="w-4 h-4" />
              {connection.target.email}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 md:w-48">
          <Button variant="outline" className="w-full" onClick={() => onViewProfile(connection)}>
            View Profile
          </Button>
          <Button
            variant="outline"
            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => onRemove(connection.targetUserId, displayName)}
            disabled={isRemoving}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Remove
          </Button>
        </div>
      </div>

      {/* Added Date */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Added on{' '}
          {new Date(connection.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: 'UTC',
          })}
        </p>
      </div>
    </Card>
  );
};
